import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Building2, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useProperties } from '../hooks/useProperties';
import { usePropertyFilters } from '../hooks/usePropertyFilters';
import PropertyCard from '../components/property/PropertyCard';
import PropertyFilters from '../components/property/PropertyFilters';

function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [favorites, setFavorites] = useState<number[]>([]);
  const navigate = useNavigate();
  const { filters: filterOptions } = usePropertyFilters();
  
  const [filters, setFilters] = useState({
    zone: searchParams.get('zone') || '',
    propertyType: searchParams.get('propertyType') || '',
    priceRange: searchParams.get('priceRange') || '',
    totalRooms: searchParams.get('totalRooms') || '',
    features: searchParams.get('features')?.split(',') || []
  });

  // Load filters from URL on initial render
  useEffect(() => {
    const newFilters: any = {};
    searchParams.forEach((value, key) => {
      newFilters[key] = key === 'features' ? value.split(',') : value;
    });
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    
    // Aggiungi solo i filtri che hanno un valore
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) newParams.set(key, value.join(','));
        } else {
          newParams.set(key, value as string);
        }
      }
    });
    
    setSearchParams(newParams);
  }, [filters]);

  // Parse price range if it exists in filters
  const getPriceRange = () => {
    if (!filters.priceRange) return { min: undefined, max: undefined };
    
    const parts = filters.priceRange.split('-');
    
    // Skip the first part if it's 'vendita' or 'affitto'
    const startIndex = ['vendita', 'affitto'].includes(parts[0]) ? 1 : 0;
    
    const minStr = parts[startIndex];
    const maxStr = parts[startIndex + 1];
    
    const min = Number(minStr);
    const max = maxStr === '0' ? Infinity : Number(maxStr);
    
    return {
      min: !isNaN(min) ? min : undefined,
      max: !isNaN(max) && max !== 0 ? max : undefined
    };
  };

  const { properties, loading, error } = useProperties({
    propertyType: filters.propertyType,
    zone: filters.zone,
    minPrice: getPriceRange().min,
    maxPrice: getPriceRange().max,
    totalRooms: filters.totalRooms
    // Non specifico statusId perché useProperties caricherà
    // automaticamente solo gli immobili pubblicati
  });

  const handleFilterChange = (newFilters: any) => {
    // Crea un nuovo oggetto filtri pulito, escludendo i valori vuoti
    const cleanedFilters = Object.entries(newFilters)
      .filter(([_, value]) => {
        // Mantieni solo i valori non vuoti
        if (value === '' || value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
    
    // Imposta i nuovi filtri
    setFilters(cleanedFilters);
  };

  const clearFilters = () => {
    // Pulisci i filtri mantenendo solo valori default
    setFilters({
      zone: '',
      propertyType: '',
      priceRange: '',
      totalRooms: '',
      features: []
    });
    
    // Pulisci anche i parametri URL
    navigate('/immobili');
  };

  const toggleFavorite = (id: number) => {
    setFavorites(prev => {
      if (prev.includes(id)) {
        return prev.filter(propId => propId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>Immobili | Molinaro Immobiliare</title>
        <meta name="description" content={`Scopri gli immobili in vendita e in affitto a Scandicci e dintorni. Appartamenti, ville, case indipendenti e soluzioni immobiliari selezionate per te.`} />
        <meta name="keywords" content={`immobili vendita, case vendita Scandicci, appartamenti vendita, ${filters.zone ? filters.zone : 'Scandicci'}`} />
        <link rel="canonical" href="https://www.molinaroimmobiliare.it/immobili" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.molinaroimmobiliare.it/immobili" />
        <meta property="og:title" content="Immobili | Molinaro Immobiliare" />
        <meta property="og:description" content="Scopri gli immobili in vendita e in affitto a Scandicci e dintorni. Appartamenti, ville, case indipendenti e soluzioni immobiliari selezionate per te." />
        <meta property="og:image" content="https://www.molinaroimmobiliare.it/images/agenzia3.jpeg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.molinaroimmobiliare.it/immobili" />
        <meta property="twitter:title" content="Immobili | Molinaro Immobiliare" />
        <meta property="twitter:description" content="Scopri gli immobili in vendita e in affitto a Scandicci e dintorni. Appartamenti, ville, case indipendenti e soluzioni immobiliari selezionate per te." />
        <meta property="twitter:image" content="https://www.molinaroimmobiliare.it/images/agenzia3.jpeg" />
      </Helmet>
      {/* Page Header */}
      <div className="bg-primary py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-4xl font-monomakh mb-4">I Nostri Immobili</h1>
            <p className="text-xl text-gray-200">
              Esplora la nostra selezione di immobili a Scandicci e dintorni
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Results */}
      <div className="container mx-auto px-4 -mt-8">
        <div className="space-y-6">
          {/* Filters */}
          <PropertyFilters
            onFilterChange={handleFilterChange}
            activeFilters={filters}
            onClearFilters={clearFilters}
          />

          {/* Results Header */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center text-gray-500 mb-2">
                  <Building2 className="w-5 h-5 mr-2 text-primary" />
                  <span>Immobili</span>
                  {filters.zone && (
                    <>
                      <span className="mx-2">•</span>
                      <MapPin className="w-5 h-5 mr-1 text-primary" />
                      <span>
                        {filterOptions?.zones?.find(z => z.value === filters.zone)?.label || 
                         filters.zone}
                      </span>
                    </>
                  )}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  {loading ? 'Ricerca in corso...' : `${properties.length} Immobili trovati`}
                </h2>
              </div>
            </div>
          </div>

          {/* Properties Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              <p className="mt-4 text-lg text-gray-600">Ricerca immobili in corso...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-red-50 rounded-lg">
              <p className="text-xl text-red-600 font-medium">{error}</p>
              <p className="mt-2 text-gray-600">Si è verificato un errore. Riprova più tardi.</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">Nessun immobile trovato</h3>
              <p className="text-gray-600 mb-6">
                {Object.values(filters).some(v => v)
                  ? 'Nessun immobile corrisponde ai criteri di ricerca selezionati.'
                  : 'Non ci sono ancora immobili disponibili.'}
              </p>
              {Object.values(filters).some(v => v) && (
                <button
                  onClick={clearFilters}
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-light transition-colors font-semibold"
                >
                  Cancella tutti i filtri
                </button>
              )}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {properties.map((property, index) => (
                <PropertyCard 
                  key={property.id}
                  property={property}
                  index={index}
                />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Properties;